import { createAsyncThunk, createSlice, type PayloadAction } from '@reduxjs/toolkit'
import {
  fetchFeature2ChargingCommandStatus,
  fetchFeature2ChargingKeys,
  startFeature2Charging,
  stopFeature2Charging,
} from '../../service/feature2-charging-steering-api'
import type {
  Feature2ChargingCommandStatusResponse,
  Feature2ChargingKeysResponse,
  Feature2ChargingStartResponse,
  Feature2ChargingStopRequest,
  Feature2ChargingStopResponse,
} from '../../types/feature2-charging-steering.types'
import type { AppError } from '../../types/error.types'
import { toAppError } from '../../utils/ErrorHandler'
import type { RootState } from '../../store'

export type Feature2ChargingAction = 'start' | 'stop'

export type Feature2ChargingCommandResponse =
  | Feature2ChargingStartResponse
  | Feature2ChargingStopResponse

export type Feature2ChargingSteeringState = {
  vin: string
  commandLoading: Feature2ChargingAction | null
  commandRequestId?: string
  statusLoading: boolean
  statusRequestId?: string
  vehicleStateLoading: boolean
  vehicleStateRequestId?: string
  resumeChargingAtEnabled: boolean
  resumeChargingAt: string
  commandIdInput: string
  lastCommandId?: string
  lastCommandAction?: Feature2ChargingAction
  commandResponse?: Feature2ChargingCommandResponse
  commandStatusResponse?: Feature2ChargingCommandStatusResponse
  vehicleStateResponse?: Feature2ChargingKeysResponse
  commandError?: AppError
  statusError?: AppError
  vehicleStateError?: AppError
}

type StopChargingRequestResult =
  | {
      success: true
      request?: Feature2ChargingStopRequest
    }
  | {
      success: false
      error: AppError
    }

const MINIMUM_RESUME_DELAY_MS = 10 * 60_000
const DEFAULT_RESUME_DELAY_MS = 15 * 60_000
const VIN_REGEX = /^[A-HJ-NPR-Z0-9]{17}$/

const getDefaultResumeChargingAt = (): string => {
  const resumeAt = new Date(Date.now() + DEFAULT_RESUME_DELAY_MS)
  resumeAt.setSeconds(0, 0)
  const tzOffsetMs = resumeAt.getTimezoneOffset() * 60_000
  return new Date(resumeAt.getTime() - tzOffsetMs).toISOString().slice(0, 16)
}

const createInvalidInputError = (code: string, message: string): AppError => ({
  code,
  message,
})

const validateVin = (vin: string): AppError | undefined => {
  if (!vin) {
    return createInvalidInputError('INVALID_VIN', 'Vehicle VIN is required.')
  }

  if (!VIN_REGEX.test(vin)) {
    return createInvalidInputError('INVALID_VIN', 'Vehicle VIN must be a valid 17-character VIN.')
  }

  return undefined
}

const buildStopChargingRequest = (
  resumeChargingAtEnabled: boolean,
  resumeChargingAt: string,
): StopChargingRequestResult => {
  if (!resumeChargingAtEnabled) {
    return { success: true }
  }

  const value = resumeChargingAt.trim()

  if (!value) {
    return {
      success: false,
      error: createInvalidInputError(
        'INVALID_RESUME_CHARGING_AT',
        'Please select a valid resume charging time.',
      ),
    }
  }

  const date = new Date(value)

  if (Number.isNaN(date.getTime())) {
    return {
      success: false,
      error: createInvalidInputError(
        'INVALID_RESUME_CHARGING_AT',
        'Please select a valid resume charging time.',
      ),
    }
  }

  if (date.getTime() < Date.now() + MINIMUM_RESUME_DELAY_MS) {
    return {
      success: false,
      error: createInvalidInputError(
        'INVALID_RESUME_CHARGING_AT',
        'Resume charging time must be at least 10 minutes in the future.',
      ),
    }
  }

  return {
    success: true,
    request: {
      resumeChargingAt: date.toISOString(),
    },
  }
}

const initialState: Feature2ChargingSteeringState = {
  vin: '',
  commandLoading: null,
  statusLoading: false,
  vehicleStateLoading: false,
  resumeChargingAtEnabled: false,
  resumeChargingAt: getDefaultResumeChargingAt(),
  commandIdInput: '',
}

export const startFeature2ChargingThunk = createAsyncThunk<
  Feature2ChargingStartResponse,
  undefined,
  { state: RootState; rejectValue: AppError }
>('feature2ChargingSteering/startCharging', async (_, { getState, rejectWithValue }) => {
  const vin = getState().feature2ChargingSteering.vin.trim()
  const vinError = validateVin(vin)

  if (vinError) {
    return rejectWithValue(vinError)
  }

  try {
    return await startFeature2Charging(vin)
  } catch (error) {
    return rejectWithValue(toAppError(error, { fallbackMessage: 'Failed to start charging.' }))
  }
})

export const stopFeature2ChargingThunk = createAsyncThunk<
  Feature2ChargingStopResponse,
  undefined,
  { state: RootState; rejectValue: AppError }
>('feature2ChargingSteering/stopCharging', async (_, { getState, rejectWithValue }) => {
  const { vin, resumeChargingAtEnabled, resumeChargingAt } = getState().feature2ChargingSteering
  const selectedVin = vin.trim()
  const vinError = validateVin(selectedVin)

  if (vinError) {
    return rejectWithValue(vinError)
  }

  const requestResult = buildStopChargingRequest(resumeChargingAtEnabled, resumeChargingAt)

  if (!requestResult.success) {
    return rejectWithValue(requestResult.error)
  }

  try {
    return await stopFeature2Charging(selectedVin, requestResult.request)
  } catch (error) {
    return rejectWithValue(toAppError(error, { fallbackMessage: 'Failed to stop charging.' }))
  }
})

export const fetchFeature2ChargingCommandStatusThunk = createAsyncThunk<
  Feature2ChargingCommandStatusResponse,
  undefined,
  { state: RootState; rejectValue: AppError }
>('feature2ChargingSteering/fetchCommandStatus', async (_, { getState, rejectWithValue }) => {
  const commandId = getState().feature2ChargingSteering.commandIdInput.trim()

  if (!commandId) {
    return rejectWithValue(
      createInvalidInputError('INVALID_COMMAND_ID', 'Charging command ID is required.'),
    )
  }

  try {
    return await fetchFeature2ChargingCommandStatus(commandId)
  } catch (error) {
    return rejectWithValue(
      toAppError(error, { fallbackMessage: 'Failed to fetch command status.' }),
    )
  }
})

export const fetchFeature2ChargingKeysThunk = createAsyncThunk<
  Feature2ChargingKeysResponse,
  undefined,
  { state: RootState; rejectValue: AppError }
>('feature2ChargingSteering/fetchChargingKeys', async (_, { getState, rejectWithValue }) => {
  const vin = getState().feature2ChargingSteering.vin.trim()
  const vinError = validateVin(vin)

  if (vinError) {
    return rejectWithValue(vinError)
  }

  try {
    return await fetchFeature2ChargingKeys(vin)
  } catch (error) {
    return rejectWithValue(toAppError(error, { fallbackMessage: 'Failed to fetch vehicle state.' }))
  }
})

const feature2ChargingSteeringSlice = createSlice({
  name: 'feature2ChargingSteering',
  initialState,
  reducers: {
    setFeature2Vin(state, action: PayloadAction<string>) {
      const nextVin = action.payload.toUpperCase()

      if (state.vin === nextVin) {
        return
      }

      state.vin = nextVin
      state.commandLoading = null
      state.commandRequestId = undefined
      state.statusLoading = false
      state.statusRequestId = undefined
      state.vehicleStateLoading = false
      state.vehicleStateRequestId = undefined
      state.commandIdInput = ''
      state.lastCommandId = undefined
      state.lastCommandAction = undefined
      state.commandResponse = undefined
      state.commandStatusResponse = undefined
      state.vehicleStateResponse = undefined
      state.commandError = undefined
      state.statusError = undefined
      state.vehicleStateError = undefined
    },
    setFeature2ResumeChargingAtEnabled(state, action: PayloadAction<boolean>) {
      state.resumeChargingAtEnabled = action.payload
    },
    setFeature2ResumeChargingAt(state, action: PayloadAction<string>) {
      state.resumeChargingAt = action.payload
    },
    setFeature2CommandIdInput(state, action: PayloadAction<string>) {
      state.commandIdInput = action.payload
    },
    clearFeature2CommandError(state) {
      state.commandError = undefined
    },
    clearFeature2StatusError(state) {
      state.statusError = undefined
    },
    clearFeature2VehicleStateError(state) {
      state.vehicleStateError = undefined
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(startFeature2ChargingThunk.pending, (state, action) => {
        state.commandLoading = 'start'
        state.commandRequestId = action.meta.requestId
        state.commandResponse = undefined
        state.commandError = undefined
      })
      .addCase(startFeature2ChargingThunk.fulfilled, (state, action) => {
        if (state.commandRequestId !== action.meta.requestId) return

        state.commandLoading = null
        state.commandRequestId = undefined
        state.lastCommandId = action.payload.id
        state.lastCommandAction = 'start'
        state.commandIdInput = action.payload.id
        state.commandResponse = action.payload
      })
      .addCase(startFeature2ChargingThunk.rejected, (state, action) => {
        if (state.commandRequestId !== action.meta.requestId) return

        state.commandLoading = null
        state.commandRequestId = undefined
        state.commandError = action.payload
      })
      .addCase(stopFeature2ChargingThunk.pending, (state, action) => {
        state.commandLoading = 'stop'
        state.commandRequestId = action.meta.requestId
        state.commandResponse = undefined
        state.commandError = undefined
      })
      .addCase(stopFeature2ChargingThunk.fulfilled, (state, action) => {
        if (state.commandRequestId !== action.meta.requestId) return

        state.commandLoading = null
        state.commandRequestId = undefined
        state.lastCommandId = action.payload.id
        state.lastCommandAction = 'stop'
        state.commandIdInput = action.payload.id
        state.commandResponse = action.payload
      })
      .addCase(stopFeature2ChargingThunk.rejected, (state, action) => {
        if (state.commandRequestId !== action.meta.requestId) return

        state.commandLoading = null
        state.commandRequestId = undefined
        state.commandError = action.payload
      })
      .addCase(fetchFeature2ChargingCommandStatusThunk.pending, (state, action) => {
        state.statusLoading = true
        state.statusRequestId = action.meta.requestId
        state.commandStatusResponse = undefined
        state.statusError = undefined
      })
      .addCase(fetchFeature2ChargingCommandStatusThunk.fulfilled, (state, action) => {
        if (state.statusRequestId !== action.meta.requestId) return

        state.statusLoading = false
        state.statusRequestId = undefined
        state.commandStatusResponse = action.payload
      })
      .addCase(fetchFeature2ChargingCommandStatusThunk.rejected, (state, action) => {
        if (state.statusRequestId !== action.meta.requestId) return

        state.statusLoading = false
        state.statusRequestId = undefined
        state.statusError = action.payload
      })
      .addCase(fetchFeature2ChargingKeysThunk.pending, (state, action) => {
        state.vehicleStateLoading = true
        state.vehicleStateRequestId = action.meta.requestId
        state.vehicleStateResponse = undefined
        state.vehicleStateError = undefined
      })
      .addCase(fetchFeature2ChargingKeysThunk.fulfilled, (state, action) => {
        if (state.vehicleStateRequestId !== action.meta.requestId) return

        state.vehicleStateLoading = false
        state.vehicleStateRequestId = undefined
        state.vehicleStateResponse = action.payload
      })
      .addCase(fetchFeature2ChargingKeysThunk.rejected, (state, action) => {
        if (state.vehicleStateRequestId !== action.meta.requestId) return

        state.vehicleStateLoading = false
        state.vehicleStateRequestId = undefined
        state.vehicleStateError = action.payload
      })
  },
})

export const {
  setFeature2Vin,
  setFeature2ResumeChargingAtEnabled,
  setFeature2ResumeChargingAt,
  setFeature2CommandIdInput,
  clearFeature2CommandError,
  clearFeature2StatusError,
  clearFeature2VehicleStateError,
} = feature2ChargingSteeringSlice.actions

export const feature2ChargingSteeringReducer = feature2ChargingSteeringSlice.reducer
